import { type SupportedLocales } from "@app/shared/utils/locales";
import { Button } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexRow } from "@design-system/Layout/Flex";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { useMemo, useState, type FC, type MouseEvent } from "react";
import {
    useSupplierTemplateQuestionnaireLocale,
    useTemplateQuestionGroups,
} from "../../data";
import { useSupplierQuestionsGroupSelectTranslations } from "./i18n";
import { QuestionGroupCreateModal } from "./QuestionGroupCreateModal";
import { QuestionGroupEditModal } from "./QuestionGroupEditModal";
import { QuestionGroupSelectTrigger } from "./QuestionGroupSelectTrigger";

export const QuestionGroupSelect: FC<{
    selectedGroupId: string | undefined;
    onSelectedGroupIdChange: (groupId: string | undefined) => void;
    onQuestionGroupCreate: ({
        englishName,
        frenchName,
    }: {
        englishName?: string;
        frenchName?: string;
    }) => void;
    locale: SupportedLocales;
}> = ({
    selectedGroupId,
    onSelectedGroupIdChange,
    onQuestionGroupCreate,
    locale,
}) => {
    const { templateQuestionGroups } = useTemplateQuestionGroups();

    const { availableLocales } = useSupplierTemplateQuestionnaireLocale();

    const { t, keys } = useSupplierQuestionsGroupSelectTranslations();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const [editQuestionGroup, setEditQuestionGroup] = useState<
        | {
              id: string;
              name: {
                  en?: string;
                  fr?: string;
              };
          }
        | undefined
    >(undefined);

    const options = useMemo(() => {
        return templateQuestionGroups.map((group) => ({
            id: group.id,
            name: group.name?.[locale] ?? t(keys.group_not_translated),
        }));
    }, [templateQuestionGroups, locale, t, keys]);

    const selectedGroup = useMemo(() => {
        if (!selectedGroupId) {
            return undefined;
        }

        return templateQuestionGroups.find(
            (group) => group.id === selectedGroupId,
        );
    }, [templateQuestionGroups, selectedGroupId]);

    const handleNewGroupClick = () => {
        setIsCreateModalOpen(true);
    };

    const handleEditGroupClick = (groupId: string) => {
        return (e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            const group = templateQuestionGroups.find(
                (group) => group.id === groupId,
            );
            setEditQuestionGroup({
                id: groupId,
                name: {
                    en: group?.name?.en ?? undefined,
                    fr: group?.name?.fr ?? undefined,
                },
            });
        };
    };

    const handleEditModalOpenChange = (open: boolean) => {
        if (!open) {
            setEditQuestionGroup(undefined);
        }
    };

    const handleValueChange = (value: string[]) => {
        const newGroupId = value[0];
        onSelectedGroupIdChange(newGroupId);
    };

    return (
        <>
            <Dropdown
                value={selectedGroupId ? [selectedGroupId] : []}
                onValueChange={handleValueChange}
            >
                <QuestionGroupSelectTrigger
                    selectedGroup={selectedGroup}
                    locale={locale}
                />
                <Dropdown.Content hasPortal>
                    <Dropdown.Item onClick={handleNewGroupClick}>
                        <Dropdown.ItemText>
                            {t(keys.new_group)}
                        </Dropdown.ItemText>
                        <Dropdown.ItemIcon name="plus" />
                    </Dropdown.Item>
                    {options.map((option) => (
                        <Dropdown.SelectableItem
                            key={option.id}
                            value={option.id}
                        >
                            <FlexRow
                                alignItems="center"
                                justifyContent="between"
                                gap="1"
                                w="full"
                            >
                                <FlexRow alignItems="center" gap="2">
                                    <Dropdown.SelectedIndicator />
                                    <Ellipsis
                                        asChild
                                        lineClamp={2}
                                        className="max-w-[200px]"
                                    >
                                        <Dropdown.ItemText>
                                            {option.name}
                                        </Dropdown.ItemText>
                                    </Ellipsis>
                                </FlexRow>
                                <Button
                                    variant="text"
                                    size="xs"
                                    onClick={handleEditGroupClick(option.id)}
                                >
                                    <Button.Icon name="edit" />
                                </Button>
                            </FlexRow>
                        </Dropdown.SelectableItem>
                    ))}
                </Dropdown.Content>
            </Dropdown>
            <QuestionGroupCreateModal
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}
                onQuestionGroupCreate={onQuestionGroupCreate}
                availableLocales={availableLocales ?? []}
            />
            {editQuestionGroup && (
                <QuestionGroupEditModal
                    open={!!editQuestionGroup}
                    onOpenChange={handleEditModalOpenChange}
                    questionGroup={editQuestionGroup}
                    availableLocales={availableLocales ?? []}
                />
            )}
        </>
    );
};
